import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  FormControlLabel, Switch, Alert,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { popupAPI, noticeAPI, assetUrl } from '../services/api';
import { Popup, PopupLinkType } from '../types';
import ImageUploadField from '../components/ImageUploadField';
import { formatKstDateTime } from '../utils/datetime';

const LINK_LABELS: Record<PopupLinkType, string> = {
  none: '이동 없음',
  notice_list: '이벤트 및 공지사항 (목록)',
  notice_detail: '이벤트 및 공지사항 (특정 공지)',
  offerwall_tnk: '미션 깨고 왕창 모으기 (오퍼월 바로 실행)',
  offerwall_adpopcorn: '보너스 미션창고 (오퍼월 바로 실행)',
};

interface FormState {
  title: string;
  imageUrl: string | null;
  linkType: PopupLinkType;
  linkNoticeId: string;
  isActive: boolean;
}
const emptyForm: FormState = { title: '', imageUrl: null, linkType: 'none', linkNoticeId: '', isActive: true };

const errorMessage = (e: any, fallback: string) => e?.response?.data?.error || fallback;

// 앱 팝업과 같은 모양의 미리보기: 가로 고정, 세로는 이미지 비율대로(너무 길면 위아래 잘림)
const PopupPreview: React.FC<{ src: string }> = ({ src }) => (
  <Box sx={{ width: 260, borderRadius: '20px', overflow: 'hidden', bgcolor: '#fff', boxShadow: 3 }}>
    <Box component="img" src={src} alt="" sx={{ display: 'block', width: '100%', height: 'auto', maxHeight: 420, objectFit: 'cover' }} />
    <Box sx={{ display: 'flex', borderTop: '1px solid #EEEEEE' }}>
      <Box sx={{ flex: 1, py: 1.5, textAlign: 'center', fontSize: 13, color: '#9E9E9E' }}>오늘 하루 보지 않기</Box>
      <Box sx={{ width: '1px', bgcolor: '#EEEEEE' }} />
      <Box sx={{ flex: 1, py: 1.5, textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#424242' }}>닫기</Box>
    </Box>
  </Box>
);

const Popups: React.FC = () => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Popup | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState('');
  const [listError, setListError] = useState('');

  const { data: popups } = useQuery({
    queryKey: ['popups'],
    queryFn: async () => (await popupAPI.getAll()).data || [],
  });

  // '특정 공지' 선택용 (활성 공지만)
  const { data: notices } = useQuery({
    queryKey: ['notices'],
    queryFn: async () => (await noticeAPI.getAll()).data || [],
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['popups'] });

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyForm);
    setFormError('');
  };

  const createMutation = useMutation({
    mutationFn: popupAPI.create,
    onSuccess: () => { invalidate(); handleClose(); },
    onError: (e) => setFormError(errorMessage(e, '저장에 실패했습니다.')),
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Popup> }) => popupAPI.update(id, data),
    onSuccess: () => { invalidate(); handleClose(); },
    onError: (e) => setFormError(errorMessage(e, '저장에 실패했습니다.')),
  });
  // 목록의 노출 스위치 (켜면 서버가 나머지 팝업을 자동으로 끔)
  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) => popupAPI.update(id, { isActive }),
    onSuccess: () => { setListError(''); invalidate(); },
    onError: (e) => setListError(errorMessage(e, '노출 변경에 실패했습니다.')),
  });
  const deleteMutation = useMutation({
    mutationFn: popupAPI.delete,
    onSuccess: invalidate,
    onError: (e) => setListError(errorMessage(e, '삭제에 실패했습니다.')),
  });

  const handleOpen = (p?: Popup) => {
    setFormError('');
    if (p) {
      setEditing(p);
      setForm({
        title: p.title,
        imageUrl: p.imageUrl,
        linkType: p.linkType,
        linkNoticeId: p.linkNoticeId ? String(p.linkNoticeId) : '',
        isActive: p.isActive,
      });
    } else {
      setEditing(null);
      setForm(emptyForm);
    }
    setOpen(true);
  };

  const handleSubmit = () => {
    const data: Partial<Popup> = {
      title: form.title.trim(),
      imageUrl: form.imageUrl || '',
      linkType: form.linkType,
      linkNoticeId: form.linkType === 'notice_detail' && form.linkNoticeId ? Number(form.linkNoticeId) : null,
      isActive: form.isActive,
    };
    if (editing) updateMutation.mutate({ id: editing.id, data });
    else createMutation.mutate(data);
  };

  const linkText = (p: Popup) => {
    if (p.linkType !== 'notice_detail') return LINK_LABELS[p.linkType] || p.linkType;
    const notice = notices?.find((n) => n.id === p.linkNoticeId);
    return `특정 공지: ${notice ? notice.title : `#${p.linkNoticeId} (비활성/삭제됨)`}`;
  };

  const saving = createMutation.isPending || updateMutation.isPending;
  const canSubmit = !!form.imageUrl && (form.linkType !== 'notice_detail' || !!form.linkNoticeId);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">팝업 관리</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>팝업 추가</Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        앱 실행 직후 노출되는 이미지 팝업입니다. 노출(ON)은 1개만 가능하며, 다른 팝업을 켜면 기존 팝업은 자동으로 꺼집니다.
        권장 이미지: 1080×1440px (3:4 세로형).
      </Alert>
      {listError && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setListError('')}>{listError}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>이미지</TableCell>
              <TableCell>이름</TableCell>
              <TableCell>클릭 시 이동</TableCell>
              <TableCell>노출</TableCell>
              <TableCell>수정일</TableCell>
              <TableCell align="center">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {popups?.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{p.id}</TableCell>
                <TableCell>
                  <Box component="img" src={assetUrl(p.imageUrl)} alt="" sx={{ width: 48, height: 64, objectFit: 'cover', borderRadius: 1, display: 'block' }} />
                </TableCell>
                <TableCell>{p.title || '(이름 없음)'}</TableCell>
                <TableCell>{linkText(p)}</TableCell>
                <TableCell>
                  <Switch
                    checked={p.isActive}
                    disabled={toggleMutation.isPending}
                    onChange={(e) => toggleMutation.mutate({ id: p.id, isActive: e.target.checked })}
                  />
                </TableCell>
                <TableCell>{formatKstDateTime(p.updatedAt)}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleOpen(p)}><EditIcon /></IconButton>
                  <IconButton size="small" color="error" onClick={() => window.confirm('팝업을 삭제하시겠습니까?') && deleteMutation.mutate(p.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!popups?.length && (
              <TableRow><TableCell colSpan={7} align="center">등록된 팝업이 없습니다.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? '팝업 수정' : '팝업 추가'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>{formError}</Alert>}
          <TextField
            label="이름 (관리용, 앱에 노출 안 됨)"
            fullWidth
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            inputProps={{ maxLength: 100 }}
            sx={{ mt: 1, mb: 2 }}
          />
          <Box sx={{ mb: 2 }}>
            <ImageUploadField
              label="팝업 이미지"
              value={form.imageUrl}
              onChange={(url) => setForm((f) => ({ ...f, imageUrl: url }))}
              renderPreview={(src) => <PopupPreview src={src} />}
              guide="권장 1080×1440px (3:4 세로형) · JPG/PNG/WEBP/GIF · 10MB 이하. 앱에서는 가로 폭에 맞추고 세로는 이미지 비율대로 표시됩니다. (너무 긴 이미지는 위아래가 잘릴 수 있음)"
            />
          </Box>
          <TextField
            select
            label="이미지 클릭 시 이동"
            fullWidth
            value={form.linkType}
            onChange={(e) => setForm((f) => ({ ...f, linkType: e.target.value as PopupLinkType }))}
            sx={{ mb: 2 }}
          >
            {(Object.keys(LINK_LABELS) as PopupLinkType[]).map((k) => (
              <MenuItem key={k} value={k}>{LINK_LABELS[k]}</MenuItem>
            ))}
          </TextField>
          {form.linkType === 'notice_detail' && (
            <TextField
              select
              label="열어줄 공지사항"
              fullWidth
              value={form.linkNoticeId}
              onChange={(e) => setForm((f) => ({ ...f, linkNoticeId: e.target.value }))}
              helperText={notices?.length ? undefined : '활성 공지사항이 없습니다. 공지사항을 먼저 작성하세요.'}
              sx={{ mb: 2 }}
            >
              {(notices || []).map((n) => (
                <MenuItem key={n.id} value={String(n.id)}>{`#${n.id} ${n.title}`}</MenuItem>
              ))}
            </TextField>
          )}
          <FormControlLabel
            control={<Switch checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />}
            label="앱에 노출"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            노출로 저장하면 기존에 노출 중인 팝업은 자동으로 꺼집니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>취소</Button>
          <Button variant="contained" disabled={!canSubmit || saving} onClick={handleSubmit}>
            {editing ? '수정' : '추가'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Popups;
